import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputProps
} from '@chakra-ui/react'
import { useState } from 'react'
import { Controller, FieldValues, useFormContext } from 'react-hook-form'
import { AttentionIcon } from '../Icons/AttentionIcon'
import { CommonFormLabel } from './CommonFormLabel'
import { FieldInputProps, getErrorMessage } from './FormTextInput'

function toHalfWidth(str: string) {
  return str.replace(/[０-９]/g, function (s) {
    return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
  })
}

type FormTextInputProps<T extends FieldValues> = {
  complementAddress?: (zipCode: string) => Promise<void>
} & FieldInputProps<T, string> &
  InputProps

export const FormZipCodeInput = <T extends FieldValues = never>({
  name,
  isRequired,
  label,
  labelIndexNumber,
  helperText,
  helpTooltipText,
  showErrorText = true,
  errorMessage,
  formControlWidth,
  w,
  complementAddress,
  ...inputProps
}: FormTextInputProps<T>): ReturnType<React.VFC> => {
  const [preValue, setPreValue] = useState('')
  const { control } = useFormContext()

  const _errorMessage = getErrorMessage(name) || errorMessage
  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={_errorMessage}
      w={formControlWidth || 'auto'}
    >
      <CommonFormLabel
        label={label}
        labelIndexNumber={labelIndexNumber}
        helpTooltipText={helpTooltipText}
        isRequired={isRequired}
      />

      <Controller
        render={({ field: { onChange, onBlur, value } }) => {
          return (
            <Input
              {...inputProps}
              w={w}
              onChange={onChange}
              value={value as string}
              onFocus={(e) => setPreValue(e.target.value)}
              onBlur={(e) => {
                const value = e.target.value
                let returnValue = value
                returnValue = returnValue.replace(/[-ー]/g, '')
                returnValue = toHalfWidth(returnValue)
                returnValue = returnValue.replace(/[^0-9]/g, '')

                // 郵便番号の形式でないとき
                if (!/^[0-9]{3}[0-9]{4}$/.test(returnValue)) {
                  onChange(returnValue)
                  onBlur()
                  return
                }

                const joinedValue = [
                  returnValue.slice(0, 3),
                  returnValue.slice(3)
                ].join('-')
                onChange(joinedValue)
                if (
                  complementAddress !== undefined &&
                  preValue !== joinedValue
                ) {
                  complementAddress(joinedValue)
                }
                onBlur()
                return
              }}
            />
          )
        }}
        control={control}
        name={name}
      />

      {showErrorText && (
        <FormErrorMessage>
          <AttentionIcon />
          {_errorMessage}
        </FormErrorMessage>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
